import { router } from '@forge/bridge'
import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router'
import { useSnapshot } from 'valtio'
import { ErrorDisplay } from '@/components/Error/Error'
import { Layout } from '@/components/Layout/Layout'
import { Loading } from '@/components/Loading/Loading'
import { QuadrantList } from '@/components/QuadrantList/QuadrantList'
import { Radar } from '@/components/Radar/Radar'
import { RadarHeader } from '@/components/Radar/RadarHeader'
import { Tags } from '@/components/Tags/Tags'
import { getTags } from '@/lib/data'
import { state, useRadarNav } from '@/lib/state'
import type { Item, RadarConfig } from '../../../types'

export default function RadarPage() {
  const {
    radarConfig,
    radarItems,
    isLoading,
    error,
    context,
    availableRadars,
    embedConfig
  } = useSnapshot(state)
  const [activeTag, setActiveTag] = useState<string | null>(null)
  const { rootRoute, radarId } = useRadarNav()
  const location = useLocation()
  const [embedData, setEmbedData] = useState<{
    config: RadarConfig
    items: Item[]
    radar?: { id: number; name: string }
  } | null>(null)
  const [embedLoading, setEmbedLoading] = useState(false)
  const [embedError, setEmbedError] = useState<Error | null>(null)

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const apiUrl = params.get('apiUrl')
    if (apiUrl && radarId) {
      if (
        !state.embedConfig ||
        state.embedConfig.apiUrl !== apiUrl ||
        state.embedConfig.radarId !== String(radarId)
      ) {
        state.embedConfig = { apiUrl, radarId: String(radarId) }
      }
    }
  }, [location.search, radarId])

  useEffect(() => {
    if (embedConfig) {
      setEmbedLoading(true)
      setEmbedError(null)
      const radarIdStr = String(radarId)
      const url = embedConfig.apiUrl.includes('?')
        ? `${embedConfig.apiUrl}&radarId=${radarIdStr}`
        : `${embedConfig.apiUrl}?radarId=${radarIdStr}`
      fetch(url)
        .then(async res => {
          if (!res.ok) {
            // Try to extract error message from response body
            let errorMsg = `Failed to fetch radar data: ${res.status}`
            try {
              const data = await res.json()
              if (data && typeof data.error === 'string') {
                errorMsg = data.error
              }
            } catch (_err) {
              // Ignore JSON parse errors, use default errorMsg
            }
            throw new Error(errorMsg)
          }
          return res.json()
        })
        .then(data => {
          if (!data.config || !data.items)
            throw new Error('Invalid radar data received')

          // Debug: log what we're getting from the embed API
          console.log('[RadarPage] Embed API response:', {
            hasConfig: !!data.config,
            hasItems: !!data.items,
            hasRadar: !!data.radar,
            radarData: data.radar,
            configKeys: data.config ? Object.keys(data.config) : []
          })

          setEmbedData({
            config: data.config,
            items: data.items,
            radar: data.radar
          })
          state.radarConfig = data.config
          state.radarItems = data.items

          // Create radar metadata for embed mode so header components can display the correct name
          const embedRadarMetadata = {
            id: Number(radarId),
            name: data.radar?.name || `Tech Radar ${radarId}`,
            config: data.config
          }
          state.availableRadars = [embedRadarMetadata]
        })
        .catch(e => {
          setEmbedError(e)
        })
        .finally(() => setEmbedLoading(false))
    } else {
      setEmbedData(null)
    }
  }, [embedConfig, radarId])

  const isEmbed = !!embedConfig
  const config = isEmbed ? embedData?.config : radarConfig
  const items = isEmbed ? (embedData?.items ?? []) : radarItems

  const loading = isEmbed ? embedLoading : isLoading
  const err = isEmbed ? embedError : error

  const tags = getTags(items as Item[])
  const filteredItems: Item[] = useMemo(() => {
    if (!items || !config) return []

    const mutableItems = [...items] as Item[]

    const nonArchivedItems = mutableItems.filter(item => !item.archived)

    if (!activeTag) return nonArchivedItems

    const normalizedActiveTag = activeTag.toUpperCase()
    return nonArchivedItems.filter(item =>
      item.tags?.some(
        (tag: string) => tag.toUpperCase() === normalizedActiveTag
      )
    )
  }, [items, activeTag, config])

  if (err) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <ErrorDisplay error={err} />
      </div>
    )
  }

  if (loading || (!isEmbed && (!availableRadars || !context))) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <Loading />
      </div>
    )
  }

  if (isEmbed && (!embedData || embedLoading)) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <Loading />
      </div>
    )
  }

  const adminLink = context?.localId
    ? context.localId === 'local-dev'
      ? '/admin/radar-list'
      : `${context.siteUrl}/compass/apps/${encodeURIComponent(`b:${btoa(context.localId.replace('techradarGlobal', 'techradarAdmin'))}`)}/admin/radar-list`
    : undefined

  const handleAdminClick = (e?: React.MouseEvent) => {
    if (context?.localId === 'local-dev') {
      if (e) e.preventDefault()
      rootRoute('admin/radar-list')
    } else if (router) {
      if (e) e.preventDefault()
      router.open(adminLink || '')
    }
  }

  const handleTagClick = (tag: string | null) => {
    setActiveTag(tag)
  }

  if (!availableRadars?.length) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-64px)] gap-4">
        <div className="text-lg text-[var(--ds-text)]">
          No radars exist yet.
        </div>
        <button
          type="button"
          onClick={handleAdminClick}
          className="px-4 py-2 rounded bg-[var(--ds-surface-secondary)] text-[var(--ds-text-link)] border border-[var(--ds-border)] hover:bg-[var(--ds-surface-hover)] cursor-pointer"
        >
          Go to Admin Page
        </button>
      </div>
    )
  }

  const renderSection = (section: string) => {
    if (!config) return null

    switch (section) {
      case 'radar':
        return (
          <div key="radar" className="w-full mx-auto">
            {config.toggles.showChart ? (
              <Radar
                items={filteredItems}
                quadrants={config.quadrants}
                rings={config.rings}
                size={config.chart.size}
              />
            ) : null}
          </div>
        )
      case 'tags':
        return (
          <div key="tags">
            {config.toggles.showTagFilter ? (
              <Tags
                tags={tags ? [...tags] : []}
                activeTag={activeTag || undefined}
                onTagClick={handleTagClick}
              />
            ) : null}
          </div>
        )
      case 'list':
        return (
          <div key="list">
            {config.toggles.showQuadrantList ? (
              <QuadrantList items={filteredItems} />
            ) : null}
          </div>
        )
      default:
        return null
    }
  }

  return (
    <>
      <RadarHeader />
      <Layout>
        <div className={`flex-1 p-6 ${isEmbed ? 'p-0' : 'p-6'}`}>
          <div className="flex flex-col gap-6">
            {config?.sections.map((section: string) => renderSection(section))}
          </div>
        </div>
      </Layout>
    </>
  )
}
